/**
 * Access control — subject resolution.
 *
 * The ONE place the five gating axes are read from the app. Everything
 * downstream (nav visibility, route guards, `<Can>`, `useAccess`) consumes the
 * `AccessSubject` this produces and never queries for itself.
 *
 * Call this ONCE per tree, via `AccessProvider`. It costs several RTK queries;
 * more importantly, a context assembled in two places drifts. That is not
 * hypothetical here — `useNavCtx` and `useNewNavCtx` build the same NavContext
 * independently and the file already carries a comment about a gate that was
 * added to one and silently closed the item on the other. One resolver, one
 * subject.
 */
import { useMemo } from 'react'
import { useLocation, useParams } from 'react-router'
import useUser, { useUserRole } from '@/hooks/useUser'
import useSelectedOrganisation from '@/state/entities/organisations/hooks/useSelectedOrganisation'
import useSubscription from '@/hooks/useSubscription'
import { useHardwareTier } from '@/hooks/use-hardware-tier'
import { useSupportedDataProviders } from '@/components/marketing/hooks/useSupportedDataProviders'
import { useListSquareIntegrationsQuery } from '@/state/neo/neo-square-auth.api'
import { isNewUiEnabled } from '@/hooks/use-feature-flags'
import { isPaidVenue } from '@/utils/venue-access'
import morpheusApi from '@/state/morpheus/morpheus.slice'
import wifi from '@/state/wifi/wifi.slice'
import { resolveAccessibleSerials } from './venue-access'
import type { AccessSubject } from './access.types'

/** `@stampede.ai` — the internal staff domain. */
const STAFF_EMAIL_DOMAIN = '@stampede.ai'

/**
 * The oauth `role` value denoting a platform super admin. Distinct from the
 * per-org `UserRoleType` and confusingly also called "role" on the user record.
 */
const OAUTH_SUPER_ADMIN_ROLE = 0

/** Venue serial from the URL. Venue routes are always `/:orgId/venues/:serial/…`. */
function parseSerial(pathname: string): string | undefined {
  const segments = pathname.split('/').filter(Boolean)
  if (segments[1] !== 'venues') return undefined
  const serial = segments[2]
  return serial && serial.length > 0 ? serial : undefined
}

/**
 * Resolve the current user's full access subject.
 *
 * Prefer `useAccess()` in components — it reads the provider's already-resolved
 * subject. Call this directly only where the provider cannot reach (the nav
 * context builders, which run above it).
 */
export function useAccessSubject(): AccessSubject {
  const { pathname } = useLocation()
  const { org_id: orgId } = useParams<{ org_id: string }>()

  // ── Axis 1 + 2: who they are ──
  const user = useUser()
  const role = useUserRole()
  // Ownership is independent of the org role — an owner may hold no admin role
  // and still need to run their own organisation.
  const organisation = useSelectedOrganisation()

  // ── Axis 4: what they pay for ──
  const subscription = useSubscription()

  // ── Axis 5: what the account and venue actually have ──
  const serial = parseSerial(pathname)
  const { tier: hardwareTier, vendorKey } = useHardwareTier(serial)
  // `useSupportedDataProviders` rebuilds its default `[]` on every render while
  // the query is in flight, so the raw array is not reference-stable. Re-derive
  // it from a primitive key: the subject is held in context above the whole
  // app, and an unstable subject re-renders every consumer on every render.
  const { providers: rawDataProviders } = useSupportedDataProviders()
  const dataProvidersKey = rawDataProviders.join(',')
  const dataProviders = useMemo(
    () => (dataProvidersKey ? dataProvidersKey.split(',') : []),
    [dataProvidersKey]
  )
  // Network type (free/paid) is authoritative on the trinity Wi-Fi config —
  // the same record the Wi-Fi settings page edits — so it, not the morpheus
  // venue, drives the paid-only / free-only gates.
  const { data: wifiConfig } = wifi.useGetSettingsQuery(
    { serial: serial ?? '' },
    { skip: !serial }
  )
  // Org-level, not venue-level: Full Bhuna is an org report reachable with no
  // venue selected, so a per-venue check would hide it where it matters most.
  const { data: squareIntegrations } = useListSquareIntegrationsQuery(
    { organizationId: orgId ?? '' },
    { skip: !orgId }
  )
  // User-scoped, not org-scoped: the current user's own pending invites to join
  // an organisation, regardless of which org is selected.
  const { data: pendingInvitations } =
    morpheusApi.useGetPendingInvitationsQuery()

  const email = user?.email
  const isStaff = (email ?? '').toLowerCase().endsWith(STAFF_EMAIL_DOMAIN)
  const isSuperAdmin = isStaff && user?.role === OAUTH_SUPER_ADMIN_ROLE
  // Narrowed at the boundary: the selected-organisation hook is still untyped
  // upstream, and `owner_id` is all this needs.
  const ownerId = (organisation as { owner_id?: string } | null | undefined)
    ?.owner_id
  const isOrgOwner = Boolean(ownerId && user?.uid && ownerId === user.uid)
  // Left possibly-undefined on purpose: RTK hands back a stable reference for a
  // cached value and a stable `undefined` otherwise, so the memo below holds.
  // Defaulting to `{}` out here would mint a new object on every render.
  const planFeatures = subscription?.features
  const hasSquare = (squareIntegrations?.integrations?.length ?? 0) > 0
  const schemeId = parseSchemeId(pathname)
  // Memoised because `resolveAccessibleSerials` builds a new array: RTK hands
  // back a stable `location_access` reference for a cached user, so keying the
  // memo on it keeps the subject itself reference-stable. An unstable subject
  // re-renders every consumer in the tree on every render.
  const accessibleSerials = useMemo(
    () => resolveAccessibleSerials(user?.location_access),
    [user?.location_access]
  )

  // Once the user AND their org role are known, every rule that depends on
  // identity can be answered. Plan and account signals resolve alongside and
  // fail closed in the meantime, which is the documented convention.
  const isResolved = Boolean(user && role)

  return useMemo<AccessSubject>(
    () => ({
      role: role?.key,
      roleLevel: role?.legacy_id,
      email,
      isStaff,
      isSuperAdmin,
      has2FA: Boolean(user?.two_factor_enabled_at),
      isOrgOwner,
      newUiOptIn: isNewUiEnabled(),
      planFeatures: planFeatures ?? {},
      isTrial: Boolean(subscription?.subscription.is_trial),
      dataProviders,
      hasSquare,
      hardwareTier,
      vendorKey,
      isPaidVenue: isPaidVenue(wifiConfig),
      accessibleSerials,
      orgId,
      hasVenue: Boolean(serial),
      hasScheme: Boolean(schemeId),
      pendingInvitations: pendingInvitations?.length ?? 0,
      isResolved,
    }),
    [
      role?.key,
      role?.legacy_id,
      email,
      isStaff,
      isSuperAdmin,
      user?.two_factor_enabled_at,
      isOrgOwner,
      planFeatures,
      subscription?.subscription.is_trial,
      dataProviders,
      hasSquare,
      accessibleSerials,
      orgId,
      hardwareTier,
      vendorKey,
      wifiConfig,
      serial,
      schemeId,
      pendingInvitations?.length,
      isResolved,
    ]
  )
}

/**
 * Loyalty scheme id from the URL, for `/:orgId/loyalty/:schemeId/…` and the
 * venue-scoped equivalent. Duplicated deliberately rather than imported from
 * `old-nav-config.ts`: this module must not depend on the legacy nav tree,
 * which is slated for removal at newUi cutover.
 */
const NON_SCHEME_LOYALTY_SEGMENTS = new Set([
  'reports',
  'new',
  'overview',
  'customers',
  'history',
  'rewards',
  'vouchers',
  'links',
  'gift-cards',
  'member-imports',
  'preview-scheme',
  'preview-rewards',
])

function parseSchemeId(pathname: string): string | undefined {
  const segments = pathname.split('/').filter(Boolean)
  const index = segments.indexOf('loyalty')
  if (index < 0) return undefined
  const next = segments[index + 1]
  if (!next || NON_SCHEME_LOYALTY_SEGMENTS.has(next)) return undefined
  return next
}

// `EMPTY_SUBJECT` lives in `access.types.ts`, next to the interface it
// satisfies, so the pure policy stays testable without pulling in these hooks.
export { EMPTY_SUBJECT } from './access.types'
